import { getGeocode, getLatLng } from 'use-places-autocomplete'
import { CustomerAddress, CustomerDetails, LatLng } from 'shop/types'
import { createAddressString } from 'shop/utils/address'
import { CurrentLocationState } from '../types'
import type { useLandingLocation } from 'shop/hooks'

export interface GeocodedAddress {
  latLng: LatLng
  formattedAddress: string
  addressComponents: google.maps.GeocoderAddressComponent[]
}

/*===== Form Utils - DeliveryForm / PickupForm =====*/

/** Depending on what type of address is passed in the params, either geocode the address or pass values we already have. */
export const handleAddressSelect = async ({
  singleLineAddress,
  customerAddress,
  geocodedAddress
}: {
  singleLineAddress?: string
  customerAddress?: CustomerAddress
  geocodedAddress?: GeocodedAddress
}): Promise<CurrentLocationState | GeocodedAddress | void> => {
  // raw string address ready to be geocoded
  if (!!singleLineAddress) {
    return geocodeFromAddress(singleLineAddress)
  }

  // an already saved customer address
  if (!!customerAddress?.id) {
    const formattedAddress = createAddressString(customerAddress.address, {
      withCommas: true
    })
    // If the customer address has no LatLng, geocode the address.
    if (
      !customerAddress.address.coordinates?.lat ||
      !customerAddress.address.coordinates.lng
    ) {
      return geocodeFromAddress(formattedAddress)
    }
    // otherwise, return a similar object but with an addressId
    return {
      address: formattedAddress,
      addressId: customerAddress.id,
      addressComponents: [],
      latLng: customerAddress.address.coordinates
    }
  }

  // an already geocoded address
  if (!!geocodedAddress) {
    return geocodedAddress
  }
}

/** Geocode an address and return a formatted object */
export const geocodeFromAddress = async (singleLineAddress: string) => {
  return getGeocode({ address: singleLineAddress })
    .then(async (results) => {
      const latLng = await getLatLng(results[0])
      const formattedAddress = results[0].formatted_address
      const addressComponents = results[0].address_components

      return {
        formattedAddress,
        addressComponents,
        latLng
      }
    })
    .catch((error) => console.error('Geocode Error', error))
}

/** Compare a new Address to the Current Location state and ensure they match and have coordinate values ready to use. */
export const isAddressSameAsCurrentLocation = (
  currentLocation?: CurrentLocationState,
  newAddress?: string
): boolean => {
  if (!currentLocation || !newAddress) return false
  const { latLng, address } = currentLocation
  const hasLatLng = latLng.lat && latLng.lng
  const isMatchingAddress = address === newAddress

  // we are selecting the same address and we already have coordinates
  return !!(isMatchingAddress && hasLatLng)
}

/*===== END - Form Utils - DeliveryForm / PickupForm =====*/

export const convertLocationToGeocodedAddress = (
  currentLocation: CurrentLocationState
): GeocodedAddress => {
  const { address, addressComponents, latLng } = currentLocation
  return {
    formattedAddress: address,
    addressComponents,
    latLng
  }
}

type AutofillLocationArgs = {
  currentLocation: CurrentLocationState | undefined
  savedCurrentLocation: CurrentLocationState | null
  customerDetails: CustomerDetails | null
  defaultLoadCustomerAddress: boolean
  handleLocationSelect: ReturnType<
    typeof useLandingLocation
  >['handleLocationSelect']
  selectCustomerAddress: ReturnType<
    typeof useLandingLocation
  >['selectCustomerAddress']
  areGoogleApisLoaded: boolean
}

/** Used to prefill the current selected location with session stored address or customer default address. */
export const autofillLocation = ({
  currentLocation,
  savedCurrentLocation,
  customerDetails,
  defaultLoadCustomerAddress,
  handleLocationSelect,
  selectCustomerAddress,
  areGoogleApisLoaded
}: AutofillLocationArgs): void => {
  // if we already have an address / location do nothing
  if (currentLocation?.address) return

  // check session storage for a previously entered GEOCODED address without an addressId
  if (savedCurrentLocation && !savedCurrentLocation.addressId) {
    // clear the location if we have a saved location with no geocoded addressComponents and no addressId,
    // as we are unable to confidently build an address without them.
    if (!savedCurrentLocation.addressComponents?.length) {
      handleLocationSelect({})
      return
    }

    // otherwise select the previously GEOCODED address
    handleLocationSelect({
      geocodedAddress: convertLocationToGeocodedAddress(savedCurrentLocation)
    })
    return
  }

  // Check for customer addresses
  if (
    !!customerDetails?.addresses?.length &&
    defaultLoadCustomerAddress &&
    areGoogleApisLoaded
  ) {
    // check session storage for previously entered customer address
    if (savedCurrentLocation && savedCurrentLocation.addressId) {
      const savedLocationCustomerAddress = customerDetails.addresses.find(
        (customerAddress) =>
          customerAddress.id === savedCurrentLocation.addressId
      )
      if (savedLocationCustomerAddress) {
        selectCustomerAddress(savedLocationCustomerAddress)
        return
      }
    }

    // otherwise take default customer address
    const defaultCustomerAddress = customerDetails.addresses.find(
      (customerAddress) => customerAddress.isDefaultShippingAddress
    )
    if (defaultCustomerAddress) {
      selectCustomerAddress(defaultCustomerAddress)
      return
    }
  }
}
